import type { StudyMissionSelectDto, StudySelectDto } from '@@types/api.types';
import { BlueLabel } from '@components/shared/blue-label.component';
import { type FC, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MeiaContextTile } from './meia-context-tile.component';
import { MeiaInvestigativeQuestionsTile } from './meia-investigative-questions-tile.component';
import { MeiaKopEditorTile } from './meia-kop-editor-tile.component';
import { MeiaKopTile } from './meia-kop-tile.component';
import { MeiaMissionFramingEditor } from './meia-mission-framing-editor.component';
import { MeiaP1AdvanceBar } from './meia-p1-advance-bar.component';
import { MeiaReferencingCampaignsTile } from './meia-referencing-campaigns-tile.component';
import { MeiaStakeholdersTile } from './meia-stakeholders-tile.component';

type MeiaP1AuthoringProps = {
  canEdit: boolean;
  currentUserId?: string;
  missions: StudyMissionSelectDto[];
  study: StudySelectDto;
};

export const MeiaP1Authoring: FC<MeiaP1AuthoringProps> = (props) => {
  const { canEdit, currentUserId, missions, study } = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedAction = searchParams.get('action');

  const handleCreateMissionActionConsumed = useCallback((): void => {
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    setSearchParams(next, { preventScrollReset: true, replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div>
        <BlueLabel
          className="text-xs"
          text="Phase 1 — Mission Framing"
        />
        <h2 className="mt-1 text-lg font-semibold text-gray-950">
          Frame the problem, then the missions that address it
        </h2>
        <p className="mt-1 text-xs text-gray-500">
          Gate G1 is soft — you can advance without approval, but required fields must be filled for every mission.
        </p>
      </div>

      {canEdit ? (
        <MeiaKopEditorTile
          missions={missions}
          study={study}
        />
      ) : (
        <MeiaKopTile
          canEdit={false}
          study={study}
        />
      )}

      <MeiaMissionFramingEditor
        canEdit={canEdit}
        missions={missions}
        onCreateMissionActionConsumed={handleCreateMissionActionConsumed}
        shouldOpenCreateMission={requestedAction === 'addMission'}
        study={study}
      />

      <MeiaContextTile
        canEdit={canEdit}
        study={study}
      />

      <MeiaReferencingCampaignsTile referencingCampaigns={study.referencingCampaigns ?? []} />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <MeiaStakeholdersTile
          canManageStakeholders={study.canManageStakeholders}
          currentUserId={currentUserId}
          studyId={study.id}
        />
        <MeiaInvestigativeQuestionsTile
          canEdit={canEdit}
          studyId={study.id}
        />
      </div>

      <MeiaP1AdvanceBar
        missions={missions}
        studyId={study.id}
      />
    </div>
  );
};
